import React, { useState, useEffect } from "react";
import { Redirect, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Heading, Group, Input, Form } from "@peracto/peracto-ui";
import { faArrowCircleLeft } from "@fortawesome/pro-regular-svg-icons/faArrowCircleLeft";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GET_ONE, useClient } from "@peracto/client";
import { toast } from "react-toastify";
import { formatAsCurrency } from "packages/peracto-catalogue/Orders/OrdersView";

interface BoschTransferUnit {
  "@id": string;
  id: number;
  sku: string;
  price: number | null;
  surcharge: number | null;
  productName: string | null;
  componentName: string | null;
  quantity: number;
  vehicleOptions: any[];
}

interface BoschTransfer {
  "@id": string;
  id: number;
  basketId: string;
  createdAt: string;
  transferUrl: string | null;
  currency: string | null;
  customerReference: string | null;
  vin: string | null;
  tecDocKTypeId: string | null;
  tecDocNTypeId: string | null;
  osVehicleKey: string | null;
  vehicleReg: string | null;
  deliveryOption: string | null;
  orderNotes: string | null;
  units: BoschTransferUnit[];
  user: string;
}

const ViewTransferContainer = ({ children }) => {
  return (
    <div className="">
      <Heading name="View Bosch Transfer">
        <div className="flex-grow-1 d-flex align-items-center justify-content-end">
          <Link className="btn btn-outline-primary" to="/bosch-transfers">
            <FontAwesomeIcon icon={faArrowCircleLeft} className="mr-2" />
            Back to Transfers
          </Link>
        </div>
      </Heading>
      {children}
    </div>
  );
};

const TransferView = ({
  match: {
    params: { id },
  },
}) => {
  const [loading, setLoading] = useState(true);
  const [transferData, setTransferData] = useState<BoschTransfer | null>(null);
  const [redirect, setRedirect] = useState<string>();

  const { client } = useClient();

  const fetchTransfer = async () => {
    setLoading(true);
    try {
      const { data } = await client(GET_ONE, "bosch-transfers", {
        id: `/bosch-transfers/${id}`,
      });

      setTransferData(data);
      setLoading(false);
    } catch (e) {
      console.error(e);
      toast.error("There was an error loading this transfer.");
      setLoading(false);
      setRedirect("/bosch-transfers");
    }
  };

  useEffect(() => {
    fetchTransfer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) {
    return (
      <ViewTransferContainer>
        <div className="card">
          <div className="card-body">Loading...</div>
        </div>
      </ViewTransferContainer>
    );
  }

  return (
    <>
      <ViewTransferContainer>
        {redirect ? (
          <Redirect to={redirect} />
        ) : (
          <>
            <Helmet>
              <title>
                Bosch Transfer{" "}
                {transferData?.basketId
                  ? `- ${transferData.basketId}`
                  : ""}{" "}
                | Peracto
              </title>
            </Helmet>
            <Form
              onSubmit={() => {}}
              autoComplete="off"
              values={{
                basketId: transferData?.basketId || "",
                createdAt: transferData?.createdAt || "",
                transferUrl: transferData?.transferUrl || "",
                currency: transferData?.currency || "",
                customerReference: transferData?.customerReference || "",
                vehicleReg: transferData?.vehicleReg || "",
                vin: transferData?.vin || "",
                tecDocKTypeId: transferData?.tecDocKTypeId || "",
                tecDocNTypeId: transferData?.tecDocNTypeId || "",
                osVehicleKey: transferData?.osVehicleKey || "",
                deliveryOption: transferData?.deliveryOption || "",
                orderNotes: transferData?.orderNotes || "",
              }}
            >
              <Group key="transferDetails" id="transferDetails" name="Transfer Details">
                <Input
                  name="basketId"
                  label="Basket ID"
                  disabled
                  testId="basketId"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="createdAt"
                  label="Created At"
                  disabled
                  testId="createdAt"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="transferUrl"
                  label="Transfer URL"
                  disabled
                  testId="transferUrl"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="currency"
                  label="Currency"
                  disabled
                  testId="currency"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="customerReference"
                  label="Customer Reference"
                  disabled
                  testId="customerReference"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="deliveryOption"
                  label="Delivery Option"
                  disabled
                  testId="deliveryOption"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="orderNotes"
                  label="Order Notes"
                  disabled
                  testId="orderNotes"
                  className="hover:cursor-not-allowed"
                />
              </Group>

              <Group key="vehicleDetails" id="vehicleDetails" name="Vehicle Details">
                <Input
                  name="vehicleReg"
                  label="Vehicle Reg"
                  disabled
                  testId="vehicleReg"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="vin"
                  label="VIN"
                  disabled
                  testId="vin"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="tecDocKTypeId"
                  label="TecDoc KType ID"
                  disabled
                  testId="tecDocKTypeId"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="tecDocNTypeId"
                  label="TecDoc NType ID"
                  disabled
                  testId="tecDocNTypeId"
                  className="hover:cursor-not-allowed"
                />
                <Input
                  name="osVehicleKey"
                  label="OS Vehicle Key"
                  disabled
                  testId="osVehicleKey"
                  className="hover:cursor-not-allowed"
                />
              </Group>
            </Form>

            {transferData?.units && transferData.units.length > 0 && (
              <div className="card mb-4">
                <div className="card-header">
                  <h5 className="mb-0">Transfer Units</h5>
                </div>
                <div className="card-body">
                  <div className="table-responsive">
                    <table className="table table-striped mb-0">
                      <thead>
                        <tr>
                          <th>SKU</th>
                          <th>Product Name</th>
                          <th>Component Name</th>
                          <th className="text-right">Quantity</th>
                          <th className="text-right">Price</th>
                          <th className="text-right">Surcharge</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transferData.units.map((unit, index) => (
                          <tr key={unit["@id"] || index}>
                            <td>{unit.sku}</td>
                            <td>{unit.productName || ""}</td>
                            <td>{unit.componentName || ""}</td>
                            <td className="text-right">{unit.quantity}</td>
                            <td className="text-right">
                              {unit.price != null
                                ? formatAsCurrency(unit.price, transferData?.currency || "GBP")
                                : ""}
                            </td>
                            <td className="text-right">
                              {unit.surcharge != null
                                ? formatAsCurrency(unit.surcharge, transferData?.currency || "GBP")
                                : ""}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </ViewTransferContainer>
    </>
  );
};

export default TransferView;
